import { useState } from "react";
import { Card } from "../../components/Card";
import type { PersonaRecord } from "../../api/personas";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "hi", label: "Hindi" },
] as const;

const EMOTION_OPTIONS = [
  { value: "", label: "None (system default)" },
  { value: "calm", label: "Calm" },
  { value: "confident", label: "Confident" },
  { value: "neutral", label: "Neutral" },
  { value: "friendly", label: "Friendly" },
  { value: "serious", label: "Serious" },
  { value: "warm", label: "Warm" },
  { value: "authoritative", label: "Authoritative" },
  { value: "curious", label: "Curious" },
  { value: "empathetic", label: "Empathetic" },
  { value: "professional", label: "Professional" },
] as const;

const PROVIDER_OPTIONS = [
  { value: "cartesia", label: "Cartesia" },
] as const;

type VoiceFields = Pick<
  PersonaRecord,
  | "voice_clone_id"
  | "voice_clone_provider"
  | "tts_speed"
  | "tts_volume"
  | "tts_emotion"
  | "background_ambience_gain"
  | "language"
>;

interface VoiceRefCardProps {
  record: VoiceFields;
  onUpdate?: (fields: {
    voice_clone_id?: string | null;
    voice_clone_provider?: string | null;
    tts_speed?: number | null;
    tts_volume?: number | null;
    tts_emotion?: string | null;
    background_ambience_gain?: number | null;
    language?: "en" | "es" | "hi";
  }) => void;
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span
      style={{
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-muted)",
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function SliderField({
  label,
  value,
  fallback,
  min,
  max,
  step,
  unit,
  onChange,
  onCommit,
}: {
  label: string;
  value: number | null;
  fallback: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: number) => void;
  onCommit: (v: number) => void;
}) {
  const display = value ?? fallback;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <FieldLabel>{label}</FieldLabel>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-primary)",
            minWidth: 36,
            textAlign: "right",
          }}
        >
          {display.toFixed(1)}
          {unit ?? ""}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 9,
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {min}
        </span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={display}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          onPointerUp={(e) =>
            onCommit(parseFloat((e.target as HTMLInputElement).value))
          }
          style={{
            flex: 1,
            accentColor: "var(--accent)",
            height: 4,
            cursor: "pointer",
          }}
        />
        <span
          style={{
            fontSize: 9,
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {max}
        </span>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--bg-input)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "7px 10px",
  color: "var(--text-primary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  outline: "none",
};

export function VoiceRefCard({ record, onUpdate }: VoiceRefCardProps) {
  const hasVoice = record.voice_clone_id !== null;

  const [localSpeed, setLocalSpeed] = useState<number | null>(null);
  const [localVolume, setLocalVolume] = useState<number | null>(null);
  const [localAmbience, setLocalAmbience] = useState<number | null>(null);
  const [editVoiceId, setEditVoiceId] = useState<string | null>(null);

  function commitSpeed(val: number) {
    setLocalSpeed(null);
    onUpdate?.({ tts_speed: val });
  }

  function commitVolume(val: number) {
    setLocalVolume(null);
    onUpdate?.({ tts_volume: val });
  }

  function commitAmbience(val: number) {
    setLocalAmbience(null);
    onUpdate?.({ background_ambience_gain: val });
  }

  function commitEmotion(val: string) {
    onUpdate?.({ tts_emotion: val || null });
  }

  function commitLanguage(val: string) {
    onUpdate?.({ language: (val || "en") as "en" | "es" | "hi" });
  }

  function commitVoiceId() {
    const trimmed = (editVoiceId ?? "").trim();
    if (trimmed && trimmed !== record.voice_clone_id) {
      onUpdate?.({
        voice_clone_id: trimmed,
        voice_clone_provider: record.voice_clone_provider ?? "cartesia",
      });
    } else if (!trimmed && record.voice_clone_id) {
      onUpdate?.({ voice_clone_id: null, voice_clone_provider: null });
    }
    setEditVoiceId(null);
  }

  function commitProvider(val: string) {
    onUpdate?.({ voice_clone_provider: val || null });
  }

  return (
    <Card title="Voice configuration">
      {/* Provider + Voice ID — always editable */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Provider dropdown */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <FieldLabel>Provider</FieldLabel>
          <select
            value={record.voice_clone_provider ?? "cartesia"}
            onChange={(e) => commitProvider(e.target.value)}
            style={{ ...inputStyle, cursor: "pointer" }}
          >
            {PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Voice ID — editable text input */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <FieldLabel>Voice ID</FieldLabel>
          <input
            value={editVoiceId ?? record.voice_clone_id ?? ""}
            onChange={(e) => setEditVoiceId(e.target.value)}
            onFocus={() => setEditVoiceId(record.voice_clone_id ?? "")}
            onBlur={commitVoiceId}
            placeholder="e.g. ee23834a-b116-48a4-b136-4ae7d51591dd"
            style={{
              ...inputStyle,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          />
          {!hasVoice && (
            <span
              style={{
                fontSize: 9,
                color: "var(--error)",
                fontStyle: "italic",
              }}
            >
              Required for ACTIVE voice personas
            </span>
          )}
        </div>
      </div>

      {/* TTS generation config — only for Cartesia */}
      {hasVoice &&
        (record.voice_clone_provider ?? "cartesia") === "cartesia" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              marginTop: 4,
            }}
          >
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                color: "var(--text-muted)",
                borderBottom: "1px solid var(--border-subtle)",
                paddingBottom: 4,
              }}
            >
              Generation config
            </div>

            <SliderField
              label="Speed"
              value={localSpeed ?? record.tts_speed}
              fallback={1.2}
              min={0.6}
              max={1.5}
              step={0.1}
              unit="x"
              onChange={(v) => setLocalSpeed(v)}
              onCommit={commitSpeed}
            />

            <SliderField
              label="Volume"
              value={localVolume ?? record.tts_volume}
              fallback={1.0}
              min={0.5}
              max={2.0}
              step={0.1}
              onChange={(v) => setLocalVolume(v)}
              onCommit={commitVolume}
            />

            {/* Emotion dropdown */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Emotion</FieldLabel>
              <select
                value={record.tts_emotion ?? ""}
                onChange={(e) => commitEmotion(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {EMOTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Language dropdown */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Language</FieldLabel>
              <select
                value={record.language ?? "en"}
                onChange={(e) => commitLanguage(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                fontSize: 9,
                color: "var(--text-muted)",
                fontStyle: "italic",
              }}
            >
              Changes save automatically.
            </div>
          </div>
        )}

      {/* Call environment — office ambience bed. Applies to all voice calls,
          independent of voice-clone config, so it lives outside the
          Cartesia-only generation-config block above. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          marginTop: 16,
        }}
      >
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            color: "var(--text-muted)",
            borderBottom: "1px solid var(--border-subtle)",
            paddingBottom: 4,
          }}
        >
          Call environment
        </div>

        <SliderField
          label="Office ambience"
          value={localAmbience ?? record.background_ambience_gain}
          fallback={0}
          min={0}
          max={3}
          step={0.1}
          onChange={(v) => setLocalAmbience(v)}
          onCommit={commitAmbience}
        />

        <div
          style={{
            fontSize: 9,
            color: "var(--text-muted)",
            fontStyle: "italic",
          }}
        >
          Office-noise level — 0 = off, 2 ≈ normal office, 3 = loud. Changes
          save automatically.
        </div>
      </div>
    </Card>
  );
}
